/**
 * The customer list.
 *
 * Customers not yet live lead: something on their go-live checklist is still
 * ours to do, and every day it sits there is a day that customer is not
 * using the product they were sold.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { sortOrganizations, useOrganizations, type ConsoleOrg } from './consoleAuth';
import { isCallFailure } from '../../lib/callable';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import own from './Console.module.css';
import { FILTER_MAX } from '../../types/fieldLimits';

function statusClass(status: string): string {
  if (status === 'active') return own.pillActive ?? '';
  if (status === 'draft') return own.pillDraft ?? '';
  return own.pillOther ?? '';
}

/** A Firestore Timestamp, ISO string or epoch, as a plain date. */
function formatCreated(value: unknown): string {
  let date: Date | null = null;
  if (typeof value === 'string') date = new Date(value);
  else if (typeof value === 'object' && value !== null) {
    const v = value as { toDate?: () => Date; _seconds?: number; seconds?: number };
    if (typeof v.toDate === 'function') date = v.toDate();
    else if (typeof v._seconds === 'number') date = new Date(v._seconds * 1000);
    else if (typeof v.seconds === 'number') date = new Date(v.seconds * 1000);
  }
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

export function OrganizationsPage() {
  const { session } = useAuth();
  const { data, isLoading, error } = useOrganizations(Boolean(session));
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    const all = sortOrganizations(data?.organizations ?? []);
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (o: ConsoleOrg) =>
        o.name.toLowerCase().includes(q) ||
        o.orgId.toLowerCase().includes(q) ||
        (o.primaryDomain ?? '').toLowerCase().includes(q),
    );
  }, [data, query]);

  if (isLoading) return <Spinner label="Loading organizations" />;

  if (error) {
    return (
      <PageState title="Could not load organizations" tone="alert">
        {isCallFailure(error) ? error.message : 'The list did not come back. Reload the page to try again.'}
      </PageState>
    );
  }

  const all = data?.organizations ?? [];
  const notLive = all.filter((o) => o.status === 'active' && o.live === false).length;

  return (
    <>
      <h1 className={own.title}>Organizations</h1>
      <p className={own.subtitle}>
        Every customer on the system.
        {notLive > 0 && (
          <>
            {' '}
            <strong>
              {notLive} {notLive === 1 ? 'is' : 'are'} not live yet
            </strong>{' '}
            — something on the go-live checklist is still to do.
          </>
        )}
      </p>

      <div style={{ display: 'flex', gap: 'var(--sp-3)', marginBottom: 'var(--sp-4)', alignItems: 'center' }}>
        <label className="vh" htmlFor="org-filter">
          Filter organizations by name, id or domain
        </label>
        <input
          id="org-filter"
            maxLength={FILTER_MAX}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by name, id or domain"
          style={{
            flex: 1, padding: '9px 12px', border: '1px solid var(--line)',
            borderRadius: 'var(--radius-sm)', fontSize: 14,
          }}
        />
        <Link to="/console/orgs/new" className={own.googleBtn} style={{ width: 'auto', padding: '9px 16px', textDecoration: 'none' }}>
          New organization
        </Link>
      </div>

      <div className={own.panel}>
        {all.length === 0 ? (
          <PageState title="No organizations yet">
            Each customer is an organization. Use New organization above when a contract is
            agreed, and its onboarding starts from there.
          </PageState>
        ) : rows.length === 0 ? (
          <PageState title={`No organization matches "${query.trim()}"`}>
            Search covers the name, the id and the domain. Clear the search to see all {all.length}.
          </PageState>
        ) : (
          <table className={own.table}>
            <caption>
              {rows.length} of {all.length} organizations, not-live first
            </caption>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Id</th>
                <th scope="col">Domain</th>
                <th scope="col">Status</th>
                <th scope="col">Reminders</th>
                <th scope="col">Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((org) => (
                <tr key={org.orgId}>
                  <td>
                    <Link to={`/console/orgs/${org.orgId}`} className={own.link}>
                      {org.name}
                    </Link>
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--muted)' }}>
                    {org.orgId}
                  </td>
                  <td style={{ color: 'var(--ink-2)' }}>
                    {org.primaryDomain ?? <span style={{ color: 'var(--faint)' }}>—</span>}
                  </td>
                  <td>
                    <span className={`${own.pill} ${statusClass(org.status)}`}>{org.status}</span>
                    {org.status === 'active' && org.live === false && (
                      <span className={`${own.pill} ${own.pillDraft}`} style={{ marginLeft: 6 }}>not live</span>
                    )}
                  </td>
                  <td>
                    {/* Per-org and default off. A customer with reminders off
                        gets no acknowledgement chasing at all, which looks
                        like a broken product from their side. */}
                    {org.remindersEnabled ? 'On' : <span style={{ color: 'var(--faint)' }}>Off</span>}
                  </td>
                  <td className="tnum">{formatCreated(org.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
